import { Component, OnDestroy, OnInit } from '@angular/core';
import { finalize, forkJoin, Subject, takeUntil } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute } from '@angular/router';
import { Dayjs } from 'dayjs';
import { CoachCalendarService } from '../../core/services/coach-calendar.service';
import {
  IAppointment,
  IAvailabilitySlot,
  ICoachCalendarSettings,
  IRescheduleRequest,
} from '../../core/models/appointment.model';
import { EAppointmentStatus } from '../../core/enums/appointment-status.enum';
import { ERescheduleRequestStatus } from '../../core/enums/reschedule-request-status.enum';
import { startOfMonth, startOfWeek, tz } from '../../core/helpers/calendar.helper';
import { CoachRescheduleService } from '../../core/services/coach-reschedule.service';
import {
  RescheduleRequestsModalComponent
} from './reschedule-requests-modal/reschedule-requests-modal.component';
import { AvailabilityModalComponent } from './availability-modal/availability-modal.component';

type AgendaView = 'week' | 'month';

@Component({
  selector: 'coach-agenda',
  templateUrl: './agenda.component.html',
  styleUrl: './agenda.component.scss',
})
export class AgendaComponent implements OnInit, OnDestroy {

  /** The daily plan is what a coach opens the panel for, so it leads. */
  activeTab: 'today' | 'calendar' = 'today';
  view: AgendaView = 'week';

  rangeStart: Dayjs = startOfWeek();
  appointments: IAppointment[] = [];
  availability: IAvailabilitySlot[] = [];
  settings?: ICoachCalendarSettings;
  /** Only what is still waiting on the coach; the rest is in the "all requests" dialog. */
  rescheduleRequests: IRescheduleRequest[] = [];
  rescheduleIsLoading = true;
  busyRequestId?: number;
  /** Bumped to tell the day's plan to reload after a request was answered. */
  dayRefreshToken = 0;

  isLoading = false;
  /** The first load: settings and availability, without which no grid can be drawn. */
  baseIsLoading = true;

  destroyed$ = new Subject();

  /** Set when the coach arrives from a student's header via "Görüşme Planla". */
  preselectedStudentId?: number;

  constructor(
    private calendarService: CoachCalendarService,
    private rescheduleService: CoachRescheduleService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
  ) {
  }

  ngOnInit(): void {
    this.readQueryParams();
    this.getBaseData();
    this.getAppointments();
    this.getRescheduleRequests();

    // Answering a request moves a real meeting, so every list on the page is stale afterwards —
    // including the day's plan, which loads its own appointments.
    this.rescheduleService.changed$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.getRescheduleRequests();
        this.getAppointments();
        this.dayRefreshToken += 1;
      });
  }

  /** Planning for a student starts on the calendar, where a free slot can be picked. */
  readQueryParams() {
    const params = this.route.snapshot.queryParams;
    const studentId = Number(params['studentId']);

    if (Number.isInteger(studentId) && studentId > 0) {
      this.preselectedStudentId = studentId;
    }
    if (params['tab'] === 'calendar' || this.preselectedStudentId) {
      this.activeTab = 'calendar';
    }
  }

  scheduledCount(): number {
    return this.appointments.filter(a => a.status !== EAppointmentStatus.Cancelled).length;
  }

  /** The first upcoming meeting drives the banner at the top of the page. */
  nextAppointment(): IAppointment | undefined {
    const now = tz();
    return this.appointments
      .filter(a => (a.status !== EAppointmentStatus.Cancelled) && tz(a.startsAt).isAfter(now))
      .sort((a, b) => tz(a.startsAt).valueOf() - tz(b.startsAt).valueOf())[0];
  }

  /** "12 – 18 Haziran" for a week, "Haziran 2026" for a month. */
  rangeLabel(): string {
    if (this.view === 'month') {
      return this.rangeStart.format('MMMM YYYY').toLocaleUpperCase('tr-TR');
    }

    const end = this.rangeStart.endOf('isoWeek');
    const startLabel = (this.rangeStart.month() === end.month())
      ? this.rangeStart.format('D')
      : this.rangeStart.format('D MMMM');

    return `${startLabel} – ${end.format('D MMMM')}`.toLocaleUpperCase('tr-TR');
  }

  hasAvailability(): boolean {
    return this.availability.length > 0;
  }

  getBaseData() {
    forkJoin({
      settings: this.calendarService.getSettings(),
      availability: this.calendarService.getAvailability(),
    })
      .pipe(takeUntil(this.destroyed$), finalize(() => this.baseIsLoading = false))
      .subscribe(r => {
        this.settings = r.settings.data as ICoachCalendarSettings;
        this.availability = r.availability.data as IAvailabilitySlot[];
      });
  }

  getAppointments() {
    const {from, to} = this.currentRange();

    this.isLoading = true;
    this.calendarService.getAppointments(from, to)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(r => {
        this.appointments = r.data as IAppointment[];
      });
  }

  getRescheduleRequests() {
    this.rescheduleIsLoading = true;
    this.calendarService.getRescheduleRequests(`?page=1&size=20&filter[status]=${ERescheduleRequestStatus.Pending}`)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.rescheduleIsLoading = false))
      .subscribe(r => {
        this.rescheduleRequests = (r.data?.result ?? r.data) as IRescheduleRequest[];
      });
  }

  /** The whole history, pending ones still answerable from there. */
  openAllRequests() {
    this.modalService.open(RescheduleRequestsModalComponent, {
      size: 'lg',
      centered: true,
      animation: true,
      scrollable: true,
    });
  }

  setView(view: AgendaView) {
    if (this.view === view) {
      return;
    }

    this.view = view;
    this.rangeStart = (view === 'week') ? startOfWeek(this.rangeStart) : startOfMonth(this.rangeStart);
    this.getAppointments();
  }

  moveRange(direction: -1 | 1) {
    const unit = (this.view === 'week') ? 'week' : 'month';
    this.rangeStart = this.rangeStart.add(direction, unit);
    this.getAppointments();
  }

  goToToday() {
    this.rangeStart = (this.view === 'week') ? startOfWeek() : startOfMonth();
    this.getAppointments();
  }

  /** The month grid links back into the week that holds the clicked day. */
  openWeekOf(day: Dayjs) {
    this.view = 'week';
    this.rangeStart = startOfWeek(day);
    this.getAppointments();
  }

  openMeetLink(appointment: IAppointment) {
    if (appointment.meetLink) {
      window.open(appointment.meetLink, '_blank');
    }
  }

  openAvailability() {
    const modalRef = this.modalService.open(AvailabilityModalComponent, {
      size: 'lg',
      centered: true,
      animation: true,
      scrollable: true,
    });
    modalRef.componentInstance.slots = this.availability;
    modalRef.componentInstance.appointments = this.appointments;

    modalRef.closed.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.getBaseData();
      this.getAppointments();
    });
  }

  approveReschedule(request: IRescheduleRequest) {
    this.busyRequestId = request.id;
    this.rescheduleService.approve(request)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.busyRequestId = undefined))
      .subscribe();
  }

  rejectReschedule(request: IRescheduleRequest) {
    this.busyRequestId = request.id;
    this.rescheduleService.reject(request)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.busyRequestId = undefined))
      .subscribe();
  }

  private currentRange(): { from: Date, to: Date } {
    const unit = (this.view === 'week') ? 'week' : 'month';
    // The month grid shows leading and trailing days of neighbouring months, so the range is
    // widened to the surrounding weeks — otherwise those cells would look empty.
    const from = (unit === 'week') ? this.rangeStart.startOf('isoWeek') : this.rangeStart.startOf('month').startOf('isoWeek');
    const to = (unit === 'week') ? this.rangeStart.endOf('isoWeek') : this.rangeStart.endOf('month').endOf('isoWeek');

    return {from: from.toDate(), to: to.toDate()};
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
